<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import { SOURCES_TYPES } from "@core/constants/source"
import {
    formatIdSource,
    pdlEnedisValidator,
    pdlValidator,
    pceValidator,
} from "@core/helpers"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            /** Aligné PdlFormSearch.vue */
            placeholder_pdl: "Identifiant PDL",
            hint_pdl_format_guide:
                "Saisissez le numéro PDL (PRM) : 14 chiffres. Les espaces sont ignorés.",
            hint_pdl_format_invalid:
                "Le numéro doit comporter exactement 14 chiffres.",
            hint_pdl_not_enedis:
                "Ce numéro ne correspond pas au format habituel d'un PDL Enedis (PRM). Vérifiez votre saisie.",
            /** Aligné PceFormSearch.vue */
            placeholder_pce: "Identifiant PCE",
            hint_pce_format_guide:
                "Saisissez le numéro PCE : 14 chiffres, ou les lettres GI suivies de 6 chiffres (ex. GI123456). Les espaces sont ignorés.",
            hint_pce_format_invalid:
                "Format non reconnu. Vérifiez votre saisie.",
            date_from_data_label: "Date de première donnée",
            date_from_data_placeholder: "Choisir une date",
            date_from_data_hint:
                "Date à partir de laquelle récupérer les données de consommation.",
            error: {
                duplicate: "Ce compteur est déjà associé à un autre bâtiment.",
                date_required: "La date est obligatoire pour un PCE",
                date_invalid: "La date n'est pas valide",
            },
            add: "Ajouter un compteur",
            edit: "Modifier",
            remove: "Supprimer",
            cancel: "Annuler",
            submit: "Ajouter",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
export interface BuildingSource {
    idtype: "pdl" | "pce"
    idsource: string
    date_from_data?: string
}

export interface Props {
    /**
     * Liste des sources (PDL/PCE) du bâtiment
     */
    modelValue: BuildingSource[]
    /**
     * Liste des sources en blacklist (déjà utilisées dans d'autres bâtiments)
     */
    blacklistSources?: BuildingSource[]
    /**
     * Désactive l'ajout de sources
     */
    disableAddSource?: boolean
}

interface Emits {
    /**
     * Émis lors de la mise à jour de la liste des sources
     */
    (e: "update:modelValue", value: BuildingSource[]): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    modelValue: () => [],
    blacklistSources: () => [],
    disableAddSource: false,
})

const emit = defineEmits<Emits>()

// ================================
// ÉTAT
// ================================

/**
 * Formulaire pour ajouter/modifier une source
 */
const form = ref<{
    idtype: "pdl" | "pce"
    idsource: string
    date_from_data?: string
}>({
    idtype: SOURCES_TYPES.PDL as "pdl",
    idsource: "",
    date_from_data: undefined,
})

/**
 * Indique si on est en mode édition
 */
const isEditing = ref(false)

/**
 * Source en cours d'édition
 */
const editingIndex = ref<number | null>(null)

/**
 * Liste des sources affichées
 */
const sourcesList = ref<BuildingSource[]>([])

/**
 * Indique si le formulaire est ouvert (dropdown)
 */
const isFormOpen = ref(false)

/**
 * Affiche l’erreur sur la date PCE seulement après un clic sur « Ajouter » / soumission invalide
 */
const showPceDateErrorAfterSubmit = ref(false)

// ================================
// COMPUTED
// ================================

/**
 * PDL — aligné PdlFormSearch : espaces ignorés
 */
function normalizePdlSearchInput(raw: string): string {
    return raw.trim().replace(/\s+/g, "")
}

/**
 * PCE — aligné PceFormSearch
 */
function normalizePceSearchInput(raw: string): string {
    return raw.trim().replace(/\s+/g, "")
}

/**
 * Valeur canonique PCE (GI + 6 chiffres) — aligné PceFormSearch
 */
function toCanonicalPceId(normalized: string): string {
    if (/^gi\d{6}$/i.test(normalized)) {
        return `GI${normalized.slice(2)}`
    }
    return normalized
}

/** PDL normalisé pour validation (PRM 14 chiffres) */
const normalizedPdlId = computed(() =>
    form.value.idtype === SOURCES_TYPES.PDL
        ? normalizePdlSearchInput(form.value.idsource)
        : ""
)

/** PCE normalisé puis canonique — aligné pceValidator */
const canonicalPceId = computed(() =>
    form.value.idtype === SOURCES_TYPES.PCE
        ? toCanonicalPceId(normalizePceSearchInput(form.value.idsource))
        : ""
)

/** Saisie non vide mais format PDL invalide — aligné PdlFormSearch */
const showPdlFormatInvalidHint = computed(
    () =>
        form.value.idtype === SOURCES_TYPES.PDL &&
        form.value.idsource.trim() !== "" &&
        !pdlValidator(normalizedPdlId.value)
)

/** 14 chiffres valides mais préfixe PRM hors plages Enedis — aligné PdlFormSearch */
const showPdlNotEnedisHint = computed(
    () =>
        form.value.idtype === SOURCES_TYPES.PDL &&
        pdlValidator(normalizedPdlId.value) &&
        !pdlEnedisValidator(normalizedPdlId.value)
)

/** Saisie PCE non vide mais format invalide — aligné PceFormSearch */
const showPceFormatHint = computed(
    () =>
        form.value.idtype === SOURCES_TYPES.PCE &&
        form.value.idsource.trim() !== "" &&
        !pceValidator(canonicalPceId.value)
)

/**
 * Vérifie si le formulaire est valide
 */
const getIsFormValid = computed(() => {
    if (!form.value.idsource?.trim()) return false

    if (getIsInBlacklist.value || getIsDuplicate.value) return false

    if (form.value.idtype === SOURCES_TYPES.PDL) {
        return (
            pdlValidator(normalizedPdlId.value) &&
            pdlEnedisValidator(normalizedPdlId.value)
        )
    }
    if (!pceValidator(canonicalPceId.value)) return false
    if (!form.value.date_from_data) return false
    return parseBuildingSourceDateInput(form.value.date_from_data) !== null
})

/**
 * Vérifie si la source est en blacklist
 */
const getIsInBlacklist = computed(() => {
    if (!form.value.idsource) return false
    const compareValue = createCompareValue({
        idtype: form.value.idtype,
        idsource: form.value.idsource,
    })
    return props.blacklistSources.some(
        (source) => createCompareValue(source) === compareValue
    )
})

/**
 * Vérifie si la source est déjà dans la liste
 */
const getIsDuplicate = computed(() => {
    if (!form.value.idsource) return false
    const compareValue = createCompareValue({
        idtype: form.value.idtype,
        idsource: form.value.idsource,
    })
    return sourcesList.value.some(
        (source, index) =>
            createCompareValue(source) === compareValue &&
            index !== editingIndex.value
    )
})

/**
 * Doublon / blacklist — message d’erreur dédié (hors textes PdlFormSearch / PceFormSearch)
 */
const getIdsourceDuplicateError = computed(() => {
    if (!form.value.idsource?.trim()) return ""
    if (getIsInBlacklist.value || getIsDuplicate.value) {
        return t("error.duplicate")
    }
    return ""
})

/**
 * Messages d’erreur sous le hint (BaseInputWrapper `error`, HTML avec sauts de ligne)
 */
const getIdsourceWrapperError = computed(() => {
    const parts: string[] = []
    if (getIdsourceDuplicateError.value) {
        parts.push(getIdsourceDuplicateError.value)
    }
    if (form.value.idtype === SOURCES_TYPES.PCE && showPceFormatHint.value) {
        parts.push(t("hint_pce_format_invalid"))
    }
    if (form.value.idtype === SOURCES_TYPES.PDL) {
        if (showPdlFormatInvalidHint.value) {
            parts.push(t("hint_pdl_format_invalid"))
        }
        if (showPdlNotEnedisHint.value) {
            parts.push(t("hint_pdl_not_enedis"))
        }
    }
    return parts.length > 0 ? parts.join("<br>") : ""
})

/**
 * Message d'erreur pour le champ date_from_data (après tentative de soumission)
 */
const getDateError = computed(() => {
    if (form.value.idtype !== SOURCES_TYPES.PCE) return ""
    if (!showPceDateErrorAfterSubmit.value) return ""
    if (!form.value.date_from_data?.trim()) {
        return t("error.date_required")
    }
    if (parseBuildingSourceDateInput(form.value.date_from_data) === null) {
        return t("error.date_invalid")
    }
    return ""
})

/**
 * Pont DatePicker (Date) ↔ modèle (string YYYY-MM-DD)
 */
const pceDatePickerModel = computed<Date | null>({
    get() {
        if (form.value.idtype !== SOURCES_TYPES.PCE) return null
        return parseBuildingSourceDateInput(form.value.date_from_data)
    },
    set(value: Date | string | null | undefined) {
        if (form.value.idtype !== SOURCES_TYPES.PCE) return
        form.value.date_from_data = normalizeDatePickerValueToIso(value)
    },
})

// ================================
// WATCHERS
// ================================

/**
 * Synchronise la liste des sources avec le modelValue
 */
watch(
    () => props.modelValue,
    (newValue) => {
        sourcesList.value = [...newValue]
    },
    { immediate: true, deep: true }
)

/**
 * Masque l’erreur date PCE si le type change ou si une date valide est saisie
 */
watch(
    () => [form.value.idtype, form.value.date_from_data] as const,
    ([idtype, dateFrom]) => {
        if (idtype !== SOURCES_TYPES.PCE) {
            showPceDateErrorAfterSubmit.value = false
            return
        }
        if (
            dateFrom?.trim() &&
            parseBuildingSourceDateInput(dateFrom) !== null
        ) {
            showPceDateErrorAfterSubmit.value = false
        }
    }
)

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Crée une valeur de comparaison pour une source (ids normalisés, aligné recherche PDL/PCE)
 */
function createCompareValue(source: BuildingSource): string {
    const id =
        source.idtype === SOURCES_TYPES.PCE
            ? toCanonicalPceId(normalizePceSearchInput(source.idsource))
            : normalizePdlSearchInput(source.idsource)
    return `${source.idtype}-${id}`
}

/**
 * Interprète une date stockée (YYYY-MM-DD ou chaîne parseable) en Date locale.
 */
function parseBuildingSourceDateInput(value: string | undefined): Date | null {
    if (value == null || value.trim() === "") return null
    const trimmed = value.trim()
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed)
    if (m) {
        const y = Number(m[1])
        const mo = Number(m[2]) - 1
        const d = Number(m[3])
        if (mo < 0 || mo > 11 || d < 1 || d > 31 || Number.isNaN(y)) {
            return null
        }
        const dt = new Date(y, mo, d)
        return dt.getFullYear() === y &&
            dt.getMonth() === mo &&
            dt.getDate() === d
            ? dt
            : null
    }
    const dt = new Date(trimmed)
    return Number.isNaN(dt.getTime()) ? null : dt
}

/**
 * Formate une date locale en YYYY-MM-DD (convention API).
 */
function formatLocalDateToIso(date: Date): string {
    const y = date.getFullYear()
    const mo = String(date.getMonth() + 1).padStart(2, "0")
    const d = String(date.getDate()).padStart(2, "0")
    return `${y}-${mo}-${d}`
}

/**
 * Normalise la valeur renvoyée par le DatePicker (Date ou chaîne) en YYYY-MM-DD.
 */
function normalizeDatePickerValueToIso(
    value: Date | string | null | undefined
): string | undefined {
    if (value == null) return undefined
    if (value instanceof Date) {
        return Number.isNaN(value.getTime())
            ? undefined
            : formatLocalDateToIso(value)
    }
    if (typeof value === "string" && value.trim() !== "") {
        const d = new Date(value)
        return Number.isNaN(d.getTime()) ? undefined : formatLocalDateToIso(d)
    }
    return undefined
}

/**
 * Revalide une date PCE déjà stockée en chaîne ISO locale.
 */
function buildingSourceDateStringToIso(
    value: string | undefined
): string | undefined {
    const d = parseBuildingSourceDateInput(value)
    return d ? formatLocalDateToIso(d) : undefined
}

/**
 * Réinitialise le formulaire
 */
function resetForm(): void {
    form.value = {
        idtype: SOURCES_TYPES.PDL as "pdl",
        idsource: "",
        date_from_data: undefined,
    }
    isEditing.value = false
    editingIndex.value = null
    isFormOpen.value = false
    showPceDateErrorAfterSubmit.value = false
}

/**
 * Ajoute ou modifie une source
 */
function onSubmit(): void {
    if (!getIsFormValid.value) {
        if (
            form.value.idtype === SOURCES_TYPES.PCE &&
            pceValidator(canonicalPceId.value) &&
            !getIsInBlacklist.value &&
            !getIsDuplicate.value
        ) {
            const raw = form.value.date_from_data
            const missing = !raw?.trim()
            const invalid =
                !missing && parseBuildingSourceDateInput(raw) === null
            if (missing || invalid) {
                showPceDateErrorAfterSubmit.value = true
            }
        }
        return
    }

    showPceDateErrorAfterSubmit.value = false

    const idsourceResolved =
        form.value.idtype === SOURCES_TYPES.PCE
            ? canonicalPceId.value
            : normalizedPdlId.value

    const newSource: BuildingSource = {
        idtype: form.value.idtype,
        idsource: idsourceResolved,
        date_from_data:
            form.value.idtype === SOURCES_TYPES.PCE
                ? buildingSourceDateStringToIso(form.value.date_from_data)
                : undefined,
    }

    if (isEditing.value && editingIndex.value !== null) {
        // Modification
        sourcesList.value[editingIndex.value] = newSource
    } else {
        // Ajout
        sourcesList.value.push(newSource)
    }

    emit("update:modelValue", [...sourcesList.value])
    resetForm()
}

/**
 * Supprime une source
 */
function onRemove(index: number): void {
    sourcesList.value.splice(index, 1)
    emit("update:modelValue", [...sourcesList.value])
}

/**
 * Édite une source
 */
function onEdit(index: number): void {
    const source = sourcesList.value[index]
    form.value = {
        idtype: source.idtype,
        idsource: source.idsource,
        date_from_data: source.date_from_data,
    }
    isEditing.value = true
    editingIndex.value = index
    isFormOpen.value = true
    showPceDateErrorAfterSubmit.value = false
}

/**
 * Annule l'édition
 */
function onCancel(): void {
    resetForm()
}

/**
 * Ouvre le formulaire pour ajouter une nouvelle source
 */
function onAddNew(): void {
    resetForm()
    isFormOpen.value = true
}

/**
 * Formate le label d'une source pour l'affichage
 */
function getSourceLabel(source: BuildingSource): string {
    return `${source.idtype.toUpperCase()} - ${formatIdSource(source.idsource, source.idtype)}`
}
</script>

<template>
    <div class="source-form-multi-building-item-chip flex flex-col gap-4">
        <!-- ================================ -->
        <!-- LISTE DES SOURCES -->
        <!-- ================================ -->
        <div class="flex flex-col gap-2">
            <div v-if="sourcesList.length > 0" class="flex flex-wrap gap-2">
                <div
                    v-for="(source, index) in sourcesList"
                    :key="`source-${index}`"
                    class="flex items-center gap-1"
                >
                    <BaseChip :label="getSourceLabel(source)" removable>
                        <template #removeicon>
                            <i
                                class="pi pi-times cursor-pointer"
                                @click.stop="onRemove(index)"
                            />
                        </template>
                    </BaseChip>
                    <BaseButton
                        icon="pi pi-pencil"
                        text
                        rounded
                        size="small"
                        severity="secondary"
                        @click="onEdit(index)"
                    />
                </div>
            </div>
            <p v-else class="text-sm text-gray-500">Aucun compteur ajouté</p>
        </div>

        <!-- ================================ -->
        <!-- BOUTON AJOUTER / FORMULAIRE -->
        <!-- ================================ -->
        <div class="flex flex-col gap-4">
            <BaseButton
                v-if="!isFormOpen"
                :label="t('add')"
                icon="pi pi-plus"
                severity="secondary"
                :disabled="disableAddSource"
                size="small"
                @click="onAddNew"
            />

            <!-- Formulaire conditionnel -->
            <div
                v-if="isFormOpen"
                class="flex flex-col gap-4 p-4 border border-gray-200 rounded-lg bg-white"
            >
                <h4 class="text-sm font-semibold text-gray-700 m-0">
                    {{ isEditing ? t("edit") : t("add") }}
                </h4>

                <form class="flex flex-col gap-4" @submit.prevent="onSubmit">
                    <!-- Type de source (PDL/PCE) -->
                    <div class="flex flex-col gap-2">
                        <label class="text-sm font-medium text-gray-700">
                            Type de compteur
                        </label>
                        <BaseSelectButton
                            v-model="form.idtype"
                            :options="[
                                {
                                    label: 'PDL',
                                    value: SOURCES_TYPES.PDL,
                                },
                                {
                                    label: 'PCE',
                                    value: SOURCES_TYPES.PCE,
                                },
                            ]"
                            option-label="label"
                            option-value="value"
                            :allow-empty="false"
                            class="w-full select-button-custom"
                        />
                    </div>

                    <!-- Numéro de source (BaseInputWrapper — cf. SensorFormSearch) -->
                    <BaseInputWrapper
                        input-id="source-multi-append-meter-id"
                        :label="
                            form.idtype === SOURCES_TYPES.PDL
                                ? t('placeholder_pdl')
                                : t('placeholder_pce')
                        "
                        :hint="
                            form.idtype === SOURCES_TYPES.PCE
                                ? t('hint_pce_format_guide')
                                : t('hint_pdl_format_guide')
                        "
                        :error="getIdsourceWrapperError"
                    >
                        <BaseInputText
                            v-model="form.idsource"
                            input-id="source-multi-append-meter-id"
                            fluid
                            :placeholder="
                                form.idtype === SOURCES_TYPES.PDL
                                    ? t('placeholder_pdl')
                                    : t('placeholder_pce')
                            "
                            :invalid="!!getIdsourceWrapperError"
                            :class="{ 'p-invalid': !!getIdsourceWrapperError }"
                            autocomplete="off"
                        />
                    </BaseInputWrapper>

                    <!-- Date de première donnée (PCE) — aligné PceFormSearch -->
                    <BaseInputWrapper
                        v-if="form.idtype === SOURCES_TYPES.PCE"
                        unfloat
                        input-id="source-form-multi-pce-date-from-data"
                        :label="t('date_from_data_label')"
                        :hint="t('date_from_data_hint')"
                        :error="getDateError"
                    >
                        <BaseDatePicker
                            input-id="source-form-multi-pce-date-from-data"
                            v-model="pceDatePickerModel"
                            date-format="dd/mm/yy"
                            show-icon
                            icon-display="input"
                            class="w-full"
                            :placeholder="t('date_from_data_placeholder')"
                            :max-date="new Date()"
                            :invalid="!!getDateError"
                            :class="{ 'p-invalid': !!getDateError }"
                        />
                    </BaseInputWrapper>

                    <!-- Boutons -->
                    <div class="flex gap-2 justify-end mt-2">
                        <BaseButton
                            type="button"
                            label="Annuler"
                            severity="secondary"
                            text
                            size="small"
                            @click="onCancel"
                        />
                        <BaseButton
                            type="submit"
                            :label="isEditing ? t('edit') : t('submit')"
                            size="small"
                        />
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>
